import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { DatadogService } from 'src/app/services/datadog/datadog.service';
import { UiActions } from '../actions';
import { ModalResolution, Navigate, TagLogin, TagLoginSuccess, TagPage, TagPageSuccess } from '../actions/ui.actions';
import { OnboardingProductsStore } from '../store';
import { environment } from 'src/environments/environment';
import { AccountFormatService } from 'src/app/services/account-format.service';
import { SessionService } from 'src/app/services/session.service';
import { Analytics } from 'fif-common-analytics';
import { of } from 'rxjs';
import { CapiService } from 'src/app/services/capi/capi.service';

@Injectable()
export class UIEffects {
  private taggingAdapter = new Analytics.TaggingAdapter();

  redirect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(Navigate),
      map(({ step }) => {
        this.dataDogService.addPageView(step);
      })
    );
  }, { dispatch: false });

  toggleModal$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ModalResolution),
      withLatestFrom(
        this.store.select(OnboardingProductsStore.selectors.ui)),
      map(([ a, ui ]) => {
        return UiActions.Navigate({ step: ui.redirect });
      }));
    });

  tagPage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TagPage),
      mergeMap(({ text, extras }) => this.tagPage(text, extras).pipe(
        map(() => TagPageSuccess())
      ))
    );
  });

  tagLogin$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TagLogin),
      mergeMap(({ params }) => this.tagLogin(params).pipe(
        map(() => TagLoginSuccess())
      ))
    );
  });

  constructor(
    private actions$: Actions,
    private store: Store<OnboardingProductsStore.AppState>,
    public dataDogService: DatadogService,
    public capiService: CapiService,
    public accountFormatService: AccountFormatService,
    private readonly sessionService: SessionService
  ) {}

  getAssistType() {
    const mode = environment.mode;
    let assistType: string;
    switch (mode) {
      case 'WEB':
        assistType = 'publico-autoasistido';
        break;
      case 'WEB-PF':
        assistType = 'publico-ejecutivo';
        break;
      case 'IN':
        assistType = 'privado-autoasistido';
        break;
      case 'PF':
        assistType = 'privado-ejecutivo';
        break;
    }
    return assistType;
  }

  getProductName() {
    const product = this.accountFormatService.getDescription();
    const accountName = this.accountFormatService.getName();
    let productName = 'cuenta_ahorro_clasica';
    if (accountName === 'AhorroSueldo') {
      productName = 'cuenta_ahorro_bf';
    } else if (product.includes('Falabella')) {
      productName = 'cuenta_ahorro_bf';
    } else if (product.includes('CTS')) {
      productName = 'cuenta_ahorro_cts';
    }
    return productName;
  }

  tagPage(page: string, m1: any) {
    m1 = {...m1};
    m1.product_name = this.getProductName();
    m1.assist_type = this.getAssistType();
    if (environment.mode === 'IN' || environment.mode === 'PF') {
      const { channel, intention } = this.sessionService.getAditionalPageInfoForAnalytics();
      m1.channel = channel;
      m1.intention = intention;
    }
    this.taggingAdapter.tagPage(page, m1);
    // CASO ESPECIAL, SE DEBE LLAMAR POST TRI - SE NECESITA DNI
    if (page !== 'personal-data') {
      const tagCapiResult = this.capiService.tagCapi(page);
      if (tagCapiResult) {
        tagCapiResult.subscribe(() => {});
      }
    }
    return of('page tagged');
  }

  tagLogin(params: any) {
    const m1 = {
      product_name: this.getProductName(),
      assist_type: this.getAssistType(),
      is_joint_account: params.isJointAccount
    };

    this.taggingAdapter.tagLogin({...params}, m1);
    return of('page tagged');
  }
}
